import { Integration, MetaPixelEvents } from '../types'
import { INTEGRATIONS } from '../data/integrations'
import { DEFAULT_META_PIXEL_EVENTS } from '../data/metaPixel'
import { isValidPixelId } from '../utils/metaPixel'
import { delay, refresh } from './store'
import { supabase } from './supabaseClient'

/** Proveedor del calendario global usado por todas las landings. */
export const GOOGLE_CALENDAR = 'google_calendar'

/** Proveedor del Meta Pixel global de la organización. */
export const META_PIXEL = 'meta_pixel'

export function listIntegrations(): Integration[] {
  return INTEGRATIONS
}

/**
 * TODO(OAuth): aquí se lanzará el flujo real de conexión de cada integración.
 * Por ahora no se simula ninguna conexión activa.
 */
export async function requestConnection(integrationId: string): Promise<string> {
  await delay(null, 300)
  return integrationId
}

/** Lee la configuración guardada de una integración de la organización. */
export async function getIntegration(
  organizationId: string,
  provider: string,
): Promise<{ config: Record<string, unknown>; isActive: boolean } | null> {
  const { data, error } = await supabase
    .from('integrations')
    .select('config, is_active')
    .eq('organization_id', organizationId)
    .eq('provider', provider)
    .maybeSingle()
  if (error || !data) return null
  return {
    config: (data.config ?? {}) as Record<string, unknown>,
    isActive: data.is_active !== false,
  }
}

/**
 * Conecta el Google Calendar global de la empresa y lo propaga a todas las
 * landings que usan el calendario principal, para no configurarlo una por una.
 *
 * No cambia la lógica de reserva: solo actualiza la URL que abre el botón.
 */
export async function connectGoogleCalendar(
  organizationId: string,
  bookingUrl: string,
): Promise<void> {
  const existing = await getIntegration(organizationId, GOOGLE_CALENDAR)
  const row = {
    organization_id: organizationId,
    provider: GOOGLE_CALENDAR,
    config: { bookingUrl },
    is_active: Boolean(bookingUrl),
  }
  const { error } = existing
    ? await supabase
        .from('integrations')
        .update({ config: row.config, is_active: row.is_active })
        .eq('organization_id', organizationId)
        .eq('provider', GOOGLE_CALENDAR)
    : await supabase.from('integrations').insert(row)
  if (error) {
    throw new Error(`No se ha podido guardar la integración: ${error.message}`)
  }

  await applyGlobalCalendar(organizationId, bookingUrl)
  await refresh()
}

/** Escribe la URL global en los bloques de calendario que la usan. */
export async function applyGlobalCalendar(
  organizationId: string,
  bookingUrl: string,
): Promise<void> {
  const { data, error } = await supabase
    .from('landing_blocks')
    .select('id, content')
    .eq('organization_id', organizationId)
    .eq('type', 'calendario')
  if (error || !data?.length) return

  await Promise.all(
    data.map((block: { id: string; content: Record<string, unknown> | null }) => {
      const content = (block.content ?? {}) as Record<string, unknown>
      // Las landings con calendario propio no se tocan.
      if (content.calendarSource === 'custom') return Promise.resolve()
      return supabase
        .from('landing_blocks')
        .update({ content: { ...content, globalUrl: bookingUrl } })
        .eq('id', block.id)
    }),
  )
}

/**
 * Conecta el Meta Pixel global de la organización (un único Pixel ID, las
 * distintas acciones se distinguen por EVENTO, no por Pixel) y lo propaga a
 * las landings que no tengan un Pixel propio.
 *
 * El Pixel ID nunca se guarda sin validar: si no supera `isValidPixelId` se
 * descarta y la integración queda desactivada, en vez de guardar un valor que
 * luego se insertaría sin control en un script.
 */
export async function connectMetaPixel(
  organizationId: string,
  input: { pixelId: string; enabled: boolean; events: MetaPixelEvents },
): Promise<void> {
  const trimmed = input.pixelId.trim()
  if (trimmed && !isValidPixelId(trimmed)) {
    throw new Error('Introduce un ID de Meta Pixel válido.')
  }
  const pixelId = trimmed
  const events = { ...DEFAULT_META_PIXEL_EVENTS, ...input.events }
  const isActive = input.enabled && Boolean(pixelId)
  const existing = await getIntegration(organizationId, META_PIXEL)
  const row = {
    organization_id: organizationId,
    provider: META_PIXEL,
    config: { pixelId, events },
    is_active: isActive,
  }
  const { error } = existing
    ? await supabase
        .from('integrations')
        .update({ config: row.config, is_active: row.is_active })
        .eq('organization_id', organizationId)
        .eq('provider', META_PIXEL)
    : await supabase.from('integrations').insert(row)
  if (error) {
    throw new Error(`No se ha podido guardar la integración: ${error.message}`)
  }

  await applyGlobalMetaPixel(organizationId, {
    enabled: isActive,
    pixelId: isActive ? pixelId : undefined,
    events,
  })
  await refresh()
}

/**
 * Propaga el Pixel global a las landings de la organización que usan el
 * Pixel de la organización (sin `override`).
 *
 * Igual que `applyGlobalCalendar`, escribe directamente en `landing_blocks`
 * (posición 0, donde vive la copia pública de `pageConfig`) y en su copia de
 * `organization_settings`: no existe una tabla de Meta Pixel legible por un
 * visitante anónimo, así que la landing pública solo puede recibir el Pixel
 * a través de esa copia ya usada por el resto de la configuración de página.
 */
export async function applyGlobalMetaPixel(
  organizationId: string,
  resolved: { enabled: boolean; pixelId?: string; events: MetaPixelEvents },
): Promise<void> {
  const { data: blocks, error } = await supabase
    .from('landing_blocks')
    .select('id, landing_id, style')
    .eq('organization_id', organizationId)
    .eq('position', 0)
  if (error || !blocks?.length) return

  await Promise.all(
    blocks.map(
      async (row: {
        id: string
        landing_id: string
        style: Record<string, unknown> | null
      }) => {
        const style = (row.style ?? {}) as Record<string, unknown>
        const page = (style.page ?? {}) as Record<string, unknown>
        const current = (page.metaPixel ?? {}) as { override?: boolean }
        // Las landings con Pixel propio (override) no se tocan.
        if (current.override) return
        const nextMetaPixel = { ...current, ...resolved, override: false }
        const nextPage = { ...page, metaPixel: nextMetaPixel }
        await Promise.all([
          supabase
            .from('landing_blocks')
            .update({ style: { ...style, page: nextPage } })
            .eq('id', row.id),
          (async () => {
            const { data: settings } = await supabase
              .from('organization_settings')
              .select('value')
              .eq('organization_id', organizationId)
              .eq('key', `landing_config:${row.landing_id}`)
              .maybeSingle()
            const value = (settings?.value ?? {}) as Record<string, unknown>
            await supabase.from('organization_settings').upsert(
              {
                organization_id: organizationId,
                key: `landing_config:${row.landing_id}`,
                value: { ...value, metaPixel: nextMetaPixel },
              },
              { onConflict: 'organization_id,key' },
            )
          })(),
        ])
      },
    ),
  )
}
